import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { me, refresh } from "../crud/auth.crud";
import { actions, actionTypes } from "../store/ducks/auth.duck";

const REFRESH_INTERVAL_MS = 30 * 60 * 1000;
const RETRY_DELAY_MS = 3000;
const MAX_RETRIES = 3;
const EMPRESA_URL =
  process.env.REACT_APP_FRONTEND_CASHBARBER_URL ||
  "https://painel.cashbarber.com.br";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const requiresLogin = (error) =>
  error?.response?.data?.action === "require_login";

function AuthBoot({ children }) {
  const dispatch = useDispatch();
  const { me: meData, sessionExpired } = useSelector((state) => state.auth);
  const refreshTimerRef = useRef(null);
  const hasBootedRef = useRef(false);

  const validateUserContext = (userData) =>
    userData.identity?.ctx === "pump" && userData.session?.origin === "web";

  const verifySession = async (retries = MAX_RETRIES, refreshAttempts = 3) => {
    const response = await me().catch((e) => e);

    if (!(response instanceof Error)) {
      const userData = response.data;
      if (!validateUserContext(userData)) return;

      dispatch(actions.fulfillUser(userData));
      if (userData._migrate?.clearBearer) {
        dispatch({ type: actionTypes.ClearLegacyToken });
      }
      return;
    }

    if (requiresLogin(response)) {
      dispatch(actions.sessionExpired());
      return;
    }

    if (response.response?.status !== 401) return;

    const refreshResult = await refresh().catch((e) => e);

    if (refreshResult instanceof Error && requiresLogin(refreshResult)) {
      dispatch(actions.sessionExpired());
      return;
    }

    if (!(refreshResult instanceof Error)) {
      if (refreshAttempts <= 0) {
        dispatch(actions.sessionExpired());
        return;
      }
      return verifySession(MAX_RETRIES, refreshAttempts - 1);
    }

    if (retries <= 0) return;

    await wait(RETRY_DELAY_MS);
    return verifySession(retries - 1, refreshAttempts);
  };

  const stopRefreshTimer = () => {
    if (!refreshTimerRef.current) return;
    clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = null;
  };

  const startRefreshTimer = () => {
    stopRefreshTimer();
    refreshTimerRef.current = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      refresh().catch((e) => {
        if (requiresLogin(e)) {
          dispatch(actions.sessionExpired());
        }
      });
    }, REFRESH_INTERVAL_MS);
  };

  useEffect(() => {
    if (hasBootedRef.current) return;
    hasBootedRef.current = true;
    if (meData) return;
    verifySession();
  }, []);

  useEffect(() => {
    if (!meData || sessionExpired) {
      stopRefreshTimer();
      return stopRefreshTimer;
    }

    startRefreshTimer();
    return stopRefreshTimer;
  }, [meData, sessionExpired]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!meData || sessionExpired) return;
      refresh().catch(() => {});
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [meData, sessionExpired]);

  useEffect(() => {
    if (!sessionExpired || !meData) return;
    stopRefreshTimer();
    dispatch(actions.logout());
    window.location.href = EMPRESA_URL;
  }, [sessionExpired, meData]);

  return children;
}

export default AuthBoot;
